import { useLayout } from '@react-native-community/hooks';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';

import { useFloorMapSizer } from '../FloorMapSizer';

type Props = {
  mapComponent: ReactNode;
  titleComponent: ReactNode;
  closeButtonComponent: ReactNode;
  elevatorPanelComponent: ReactNode;
};

export const MapLayoutPortrait = observer(function MapLayoutPortrait({
  mapComponent,
  closeButtonComponent,
  elevatorPanelComponent,
}: Props) {
  const pagesBox = useLayout();
  const { pagesContainer, windowHeight, windowWidth } = useFloorMapSizer();

  // makes sure that map fits width and keeps iPhone 8 proportions
  const getSizes = (): { width: number; height: number } => {
    const maxMapHeight = windowHeight - 133 - 43 - 23; // elevator panel + title + padding for top ownable button
    const maxMapWidth = windowWidth - 20;

    // ratio based on Figma file https://www.figma.com/file/3PxdzWWVKzf7JdpUqyCqQH/Ownable-Site-Layout?node-id=3498%3A75&t=LudDtFDsxRRlB2QP-0
    const sizedWidth = maxMapHeight * (352 / 486);
    const sizedHeight = maxMapWidth * (486 / 352);

    if (sizedWidth <= windowWidth) {
      return { width: sizedWidth, height: maxMapHeight };
    }

    return { width: maxMapWidth, height: sizedHeight };
  };

  const { width, height } = getSizes();

  const elevatorWidth = pagesContainer.value.width - 30; // size of close button

  useEffect(() => {
    pagesContainer.set({ height: pagesBox.height, width: pagesBox.width });
  }, [pagesBox.height, pagesBox.width]);

  return (
    <View style={styles.aligner}>
      <View onLayout={pagesBox.onLayout} style={{ width, height }}>
        <View style={styles.closeButton}>{closeButtonComponent}</View>
        {mapComponent}
        <View style={[styles.elevator, { width: elevatorWidth }]}>
          {elevatorPanelComponent}
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  aligner: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  closeButton: {
    width: 27,
    height: 27,
    position: 'absolute',
    right: 0,
    top: -30,
  },
  elevator: {
    position: 'absolute',
    height: 133,
    left: 0,
    top: -133,
  },
});
